import {
  GoogleMap,
  Polygon,
  Marker,
  Circle,
} from '@react-google-maps/api';
import { useEffect, useState, useCallback, useRef } from 'react';
import jobSiteIcon from '../../assets/img/icons/jobsite.svg';
import PolygonIcon from '../../assets/img/location-pin-icon.svg';

const options = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  disableDoubleClickZoom: true,
  gestureHandling: 'cooperative',
  mapId: process.env.REACT_APP_JOB_SITES_MAP_ID,
};

const containerStyle = {
  width: '100%',
  height: '85vh',
  borderLeft: '1px solid #e4e7eb',
  borderRadius: '0 10px 10px 0',
  position: 'relative', // Added for floating button positioning
};

const center = {
  lat: 21.424090674479555,
  lng: 39.822141261515604,
};

// Polygon styling
const polygonOptions = {
  fillColor: '#6F7AFF',
  fillOpacity: 0.2,
  strokeColor: '#6F7AFF',
  strokeWeight: 2,
  clickable: false,
  draggable: false,
  editable: true,
  geodesic: false,
  zIndex: 1,
};

// Floating button styles
const floatingButtonStyles = {
  position: 'absolute',
  top: '20px',
  right: '20px',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

// Update the buttonStyle object
const buttonStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
  transition: 'all 0.3s ease',
  backgroundColor: '#ffffff',
  color: '#666666',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

// Add new styles for the glowing animation and tutorial overlay
const glowingButtonStyle = {
  ...buttonStyle,
  animation: 'glowPulse 1s infinite',
};

// Add these styles to your component or in a separate CSS file
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes glowPulse {
    0% {
      box-shadow: 0 0 0 0 rgb(111, 255, 197);
    }
    70% {
      box-shadow: 0 0 0 10px rgb(111, 255, 197);
    }
    100% {
      box-shadow: 0 0 0 0 rgb(85, 90, 145);
    }
  }

  .tutorial-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    text-align: center;
    padding: 20px;
  }

  .tutorial-step {
    margin: 10px 0;
    padding: 15px;
    background: rgba(111, 122, 255, 0.2);
    border-radius: 8px;
    max-width: 300px;
  }

  .tutorial-button {
    margin-top: 20px;
    padding: 10px 20px;
    background: #6F7AFF;
    border: none;
    border-radius: 4px;
    color: white;
    cursor: pointer;
    transition: background 0.3s;
  }

  .tutorial-button:hover {
    background: #5A66FF;
  }
`;
document.head.appendChild(styleSheet);

const LOCAL_STORAGE_KEY = 'polygon_tutorial_preference';

const TutorialOverlay = ({ onClose, onDontShowAgain }) => (
  <div className="polygon-tutorial-overlay">
    <div className="polygon-tutorial-container">
      {/* Header Section */}
      <div className="polygon-tutorial-header">
        <div className="polygon-tutorial-icon">
          <i className="fa fa-map"></i>
        </div>
        <h3>Draw Your Location Area</h3>
        <p>Follow these steps to define your location boundary</p>
      </div>

      {/* Steps Section */}
      <div className="polygon-tutorial-steps">
        <div className="polygon-step">
          <div className="step-indicator">
            <span>1</span>
          </div>
          <div className="step-details">
            <h4>Start Drawing</h4>
            <p>Click the <i className="fa fa-map-marker"></i> button to begin</p>
          </div>
        </div>

        <div className="polygon-step">
          <div className="step-indicator">
            <span>2</span>
          </div>
          <div className="step-details">
            <h4>Create Boundary</h4>
            <p>Click on the map to place at least 3 points</p>
          </div>
        </div>

        <div className="polygon-step">
          <div className="step-indicator">
            <span>3</span>
          </div>
          <div className="step-details">
            <h4>Complete Area</h4>
            <p>Click <i className="fa fa-check"></i> to finish or <i className="fa fa-refresh"></i> to reset</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="polygon-tutorial-actions">
        <button
          className="tutorial-btn secondary"
          onClick={onClose}
        >
          Got it
        </button>
        <button
          className="tutorial-btn primary"
          onClick={onDontShowAgain}
        >
          Don't show again
        </button>
      </div>
    </div>
  </div>
);

// Add this style block to your component
const tutorialStyles = document.createElement('style');
tutorialStyles.textContent = `
  .polygon-tutorial-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 20px;
  }

  .polygon-tutorial-container {
    background: white;
    border-radius: 16px;
    padding: 32px;
    width: 100%;
    max-width: 480px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    animation: slideUpFade 0.3s ease-out;
  }

  @keyframes slideUpFade {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .polygon-tutorial-header {
    text-align: center;
    margin-bottom: 32px;
  }

  .polygon-tutorial-icon {
    width: 64px;
    height: 64px;
    background: #6F7AFF;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }

  .polygon-tutorial-icon i {
    font-size: 28px;
    color: white;
  }

  .polygon-tutorial-header h3 {
    color: #1a1a1a;
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  .polygon-tutorial-header p {
    color: #666;
    font-size: 16px;
    margin: 0;
  }

  .polygon-tutorial-steps {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 32px;
  }

  .polygon-step {
    display: flex;
    align-items: flex-start;
    padding: 16px;
    background: #f8f9ff;
    border-radius: 12px;
    transition: all 0.2s ease;
  }

  .polygon-step:hover {
    background: #f0f2ff;
    transform: translateX(8px);
  }

  .step-indicator {
    width: 36px;
    height: 36px;
    background: #6F7AFF;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    flex-shrink: 0;
  }

  .step-indicator span {
    color: white;
    font-weight: 600;
    font-size: 18px;
  }

  .step-details {
    flex: 1;
  }

  .step-details h4 {
    color: #1a1a1a;
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px 0;
  }

  .step-details p {
    color: #666;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
  }

  .step-details i {
    color: #6F7AFF;
    margin: 0 4px;
  }

  .polygon-tutorial-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
    border-top: 1px solid #eee;
    padding-top: 24px;
  }

  .tutorial-btn {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .tutorial-btn.primary {
    background: #6F7AFF;
    color: white;
  }

  .tutorial-btn.secondary {
    background: #f0f2ff;
    color: #6F7AFF;
  }

  .tutorial-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(111, 122, 255, 0.2);
  }

  .tutorial-btn:active {
    transform: translateY(0);
  }
`;

document.head.appendChild(tutorialStyles);

const TaskLocationMap = ({
  setMap,
  singleAddressMarker,
  setAddressFromMap,
  radius,
  locationType,
  onPolygonComplete,
  isEditing = false,
  existingVertices = [],
  setRadius
}) => {
  // Add a check at the beginning to normalize singleAddressMarker format
  const [normalizedMarker, setNormalizedMarker] = useState(null);

  // Normalize the marker format at component initialization and when it changes
  useEffect(() => {
    if (!singleAddressMarker) {
      setNormalizedMarker(null);
      return;
    }

    try {
      // Check if singleAddressMarker is a Google Maps LatLng object
      if (typeof singleAddressMarker.lat === 'function' && typeof singleAddressMarker.lng === 'function') {
        setNormalizedMarker({
          lat: singleAddressMarker.lat(),
          lng: singleAddressMarker.lng()
        });
      }
      // Check if it's an object with lat/lng properties
      else if (singleAddressMarker.lat !== undefined && singleAddressMarker.lng !== undefined) {
        const lat = parseFloat(singleAddressMarker.lat);
        const lng = parseFloat(singleAddressMarker.lng);

        if (!isNaN(lat) && !isNaN(lng)) {
          setNormalizedMarker({ lat, lng });
        } else {
          console.warn("Invalid coordinates in singleAddressMarker", singleAddressMarker);
          setNormalizedMarker(null);
        }
      } else {
        console.warn("Unrecognized marker format:", singleAddressMarker);
        setNormalizedMarker(null);
      }
    } catch (error) {
      console.error("Error normalizing marker:", error);
      setNormalizedMarker(null);
    }
  }, [singleAddressMarker]);

  const [paths, setPaths] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(() => {
    const preference = localStorage.getItem(LOCAL_STORAGE_KEY);
    return preference ? false : true;
  });
  const [showGlowingAnimation, setShowGlowingAnimation] = useState(false);
  const polygonRef = useRef(null);
  const circleRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const radiusChangeTimeoutRef = useRef(null); // Add a ref for the debounce timeout
  const [hasResetted, setHasResetted] = useState(false);
  console.log("Is Editing", isEditing, "Location Type", locationType, "Normalized Marker", normalizedMarker);

  // Handle map load
  const handleMapLoad = useCallback((map) => {
    setMapInstance(map);
    setMap(map);
  }, [setMap]);

  // Polygon-specific effects
  useEffect(() => {
    if (isEditing && locationType === 'polygon' && existingVertices.length >= 3) {
      const newMarkers = existingVertices.map((vertex, index) => ({
        lat: vertex[0],
        lng: vertex[1],
        sequence: index + 1
      }));
      setMarkers(newMarkers);
      setPaths([...newMarkers, newMarkers[0]]);
      setIsComplete(true);
      setIsDrawing(false);
    }
  }, [isEditing, locationType, existingVertices]);

  // Circle-specific effects
  useEffect(() => {
    if (isEditing && locationType === 'circle' && normalizedMarker) {
      if (mapInstance) {
        try {
          mapInstance.panTo(normalizedMarker);
          mapInstance.setZoom(17);
        } catch (error) {
          console.error("Error panning to marker:", error);
          mapInstance.panTo(center);
          mapInstance.setZoom(15);
        }
      }
    }
  }, [isEditing, locationType, normalizedMarker, mapInstance]);




  // Add effect to focus on the drawn location when the map is loaded or location type changes
  useEffect(() => {
    if (mapInstance && isEditing) {
      if (locationType === 'circle' && normalizedMarker) {
        // For circle type, focus on the center point
        mapInstance.panTo(normalizedMarker);
        mapInstance.setZoom(17);
      } else if (locationType === 'polygon' && markers.length >= 3) {
        // For polygon type, calculate the bounds of the polygon
        const bounds = new google.maps.LatLngBounds();
        markers.forEach(marker => {
          bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
        });

        // Fit the map to the bounds with some padding
        mapInstance.fitBounds(bounds, {
          padding: { top: 50, right: 50, bottom: 50, left: 50 }
        });
      } else if (locationType === 'polygon' && markers.length === 0 && normalizedMarker) {
        // When switching to polygon with no markers but we have a circle marker,
        // keep the focus on the circle marker's location
        mapInstance.panTo(normalizedMarker);
        mapInstance.setZoom(17);
      } else if (locationType === 'circle' && !normalizedMarker && markers.length > 0) {
        // When switching to circle with no marker but we have polygon markers,
        // calculate the center of the polygon and focus there
        const bounds = new google.maps.LatLngBounds();
        markers.forEach(marker => {
          bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
        });

        const center = bounds.getCenter();
        mapInstance.panTo(center);
        mapInstance.setZoom(17);
      } else if (locationType === 'circle' && !normalizedMarker) {
        // When switching to circle with no marker, center on a default location
        mapInstance.panTo(center);
        mapInstance.setZoom(15);
      }
    }
  }, [mapInstance, isEditing, locationType, normalizedMarker, markers, center]);

  // Show glowing animation when polygon type is selected
  useEffect(() => {
    if (locationType === 'polygon') {
      const preference = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!preference) {
        setShowTutorial(true);
      }
      setShowGlowingAnimation(true);
      const timer = setTimeout(() => setShowGlowingAnimation(false), 35000);
      return () => clearTimeout(timer);
    }
  }, [locationType]);

  // Handle map click to add points
  const handleMapClick = useCallback((e) => {
    console.log('handleMapClick', isDrawing, isComplete);
    if (!isDrawing || isComplete) return;

    if (isEditing && markers.length > 2 && !hasResetted) {
      console.warn("In editing mode, you can only drag existing vertices. Click reset to start adding new points.");
      return;
    }

    const newPoint = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
      sequence: markers.length + 1
    };

    setMarkers(prev => [...prev, newPoint]);
    setPaths(prev => [...prev, newPoint]);
  }, [markers, isDrawing, isComplete]);


  // Update markers when polygon path changes
  const updatePolygonPoints = useCallback(() => {
    if (!polygonRef.current) return;

    const polygon = polygonRef.current;
    const path = polygon.getPath();
    const newPoints = [];

    // Get all points from the path
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      newPoints.push({
        lat: point.lat(),
        lng: point.lng(),
        sequence: i + 1
      });
    }

    // Remove the last point if it's the same as the first (closing point)
    if (isComplete && newPoints.length > 0) {
      newPoints.pop();
    }

    // Update markers and paths
    setMarkers(newPoints);
    setPaths(isComplete ? [...newPoints, newPoints[0]] : newPoints);

  }, [isComplete]);

  // Set up polygon event listeners
  const onPolygonLoad = useCallback(polygon => {
    polygonRef.current = polygon;

    // Add listeners for all types of path changes
    const path = polygon.getPath();
    path.addListener('insert_at', updatePolygonPoints);
    path.addListener('remove_at', updatePolygonPoints);
    path.addListener('set_at', updatePolygonPoints);

    // If we're in edit mode, initialize the polygon with the existing markers
    if (isEditing && locationType === 'polygon' && markers.length >= 3) {
      // Clear existing path
      path.clear();

      // Add vertices to the path
      markers.forEach(marker => {
        path.push(new google.maps.LatLng(marker.lat, marker.lng));
      });

      // Close the polygon by adding the first point again
      if (markers.length > 0) {
        path.push(new google.maps.LatLng(markers[0].lat, markers[0].lng));
      }

      // Update the polygon
      polygon.setPath(path);
      setIsComplete(true);
      setIsDrawing(false);
    }
  }, [updatePolygonPoints, isEditing, locationType, markers]);


  // Reset to circle
  const handleResetToCircle = useCallback(() => {
    setPaths([]);
    setMarkers([]);
    setIsDrawing(false);
    setIsComplete(false);

    // set the circle center
    setRadius(radius || 100);

  }, [radius]);

  // if type changes to circle, then reset the map
  useEffect(() => {
    if (locationType === 'circle') {
      handleResetToCircle();
    }
  }, [locationType, handleResetToCircle]);

  const handleTutorialClose = (dontShowAgain = false) => {
    setShowTutorial(false);
    if (dontShowAgain) {
      localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    }
  };

  // Polygon-specific handlers
  const polygonHandlers = {
    handleStartDrawing: useCallback(() => {
      console.log("handleStartDrawing", isDrawing, isComplete);
      setIsDrawing(true);
      setIsComplete(false);
      setShowGlowingAnimation(false);
    }, []),

    handleCompletePolygon: useCallback(() => {
      if (markers.length >= 3) {
        const completedPaths = [...markers, markers[0]];
        setPaths(completedPaths);
        setIsComplete(true);
        setIsDrawing(false);

        const vertices = markers.map(marker => ([
          marker.lat,
          marker.lng
        ]));

        onPolygonComplete(vertices);
      }
    }, [markers, onPolygonComplete]),

    handlePolygonReset: useCallback(() => {
      setPaths([]);
      setMarkers([]);
      setIsDrawing(false);
      setIsComplete(false);
      onPolygonComplete([]);
      setHasResetted(true);
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    }, [onPolygonComplete]),

    updatePolygonPoints: useCallback(() => {
      if (!polygonRef.current) return;
      const polygon = polygonRef.current;
      const path = polygon.getPath();
      const newPoints = [];

      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        newPoints.push({
          lat: point.lat(),
          lng: point.lng(),
          sequence: i + 1
        });
      }

      if (isComplete && newPoints.length > 0) {
        newPoints.pop();
      }

      setMarkers(newPoints);
      setPaths(isComplete ? [...newPoints, newPoints[0]] : newPoints);

      // Send updated vertices to parent
      const vertices = newPoints.map(point => ([
        point.lat,
        point.lng
      ]));
      onPolygonComplete(vertices);
    }, [isComplete, onPolygonComplete])
  };

  // Circle-specific handlers
  const circleHandlers = {
    handleCircleUpdate: useCallback((e) => {
      if (e.latLng) {
        const newPosition = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        };
        setAddressFromMap({ latLng: newPosition });
      }
    }, [setAddressFromMap]),

    handleCircleRadiusChange: useCallback(() => {
      if (circleRef.current) {
        const newRadius = circleRef.current.getRadius();

        // Clear any existing timeout
        if (radiusChangeTimeoutRef.current) {
          clearTimeout(radiusChangeTimeoutRef.current);
        }

        // Set a new timeout to debounce the radius change
        radiusChangeTimeoutRef.current = setTimeout(() => {
          // Call the setRadius prop to update the radius in the parent component
          if (setRadius) {
            // Only update if the radius has actually changed by more than 5 meters
            // This prevents excessive updates for small changes
            if (Math.abs(Math.round(newRadius) - radius) > 5) {
              // dont add any decimal places
              setRadius(Math.round(newRadius));
            }
          }
        }, 500); // 500ms debounce
      }
    }, [setRadius, radius]),

    onCircleLoad: useCallback((circle) => {
      circleRef.current = circle;
    }, [])
  };

  const renderPolygon = () => {
    if (locationType !== 'polygon') return null;

    return (
      <>
        {paths.length > 0 && (
          <Polygon
            paths={paths}
            options={{
              ...polygonOptions,
              editable: !isComplete,
              draggable: false,
            }}
            onLoad={onPolygonLoad}
            onMouseUp={polygonHandlers.updatePolygonPoints}
            onDragEnd={polygonHandlers.updatePolygonPoints}
          />
        )}
        {markers.map((marker, index) => (
          <Marker
            key={`marker-${index}`}
            position={marker}
            icon={{
              url: PolygonIcon,
              scaledSize: new google.maps.Size(30, 30),
            }}
          />
        ))}
      </>
    );
  };

  const renderCircle = () => {
    if (locationType !== 'circle' || !normalizedMarker) return null;

    return (
      <>
        <Marker
          position={normalizedMarker}
          icon={{
            url: jobSiteIcon,
            scale: 20,
          }}
          draggable={true}
          onDragEnd={circleHandlers.handleCircleUpdate}
        />
        <Circle
          center={normalizedMarker}
          radius={radius}
          options={{
            fillColor: '#6F7AFF',
            fillOpacity: 0.2,
            strokeColor: '#6F7AFF',
            strokeWeight: 2,
            clickable: false,
            draggable: true,
            editable: true,
            zIndex: 1,
          }}
          onLoad={circleHandlers.onCircleLoad}
          onRadiusChanged={circleHandlers.handleCircleRadiusChange}
        />
      </>
    );
  };

  // Add cleanup for the radius change timeout
  useEffect(() => {
    return () => {
      if (radiusChangeTimeoutRef.current) {
        clearTimeout(radiusChangeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      {showTutorial && locationType === 'polygon' && !isEditing && (
        <TutorialOverlay
          onClose={() => handleTutorialClose(false)}
          onDontShowAgain={() => handleTutorialClose(true)}
        />
      )}

      {/* Polygon control buttons */}
      {locationType === 'polygon' && (
        <div style={floatingButtonStyles}>
          <button
            onClick={polygonHandlers.handleStartDrawing}
            disabled={isDrawing}
            style={showGlowingAnimation && !isDrawing ? glowingButtonStyle : buttonStyle}
            title="Start Drawing"
          >
            <i className="fa fa-map-marker" style={{ fontSize: '18px' }} />
          </button>
          {isDrawing && markers.length >= 3 && (
            <button
              onClick={polygonHandlers.handleCompletePolygon}
              style={buttonStyle}
              title="Complete Polygon"
            >
              <i className="fa fa-check" style={{ fontSize: '18px' }} />
            </button>
          )}
          {(markers.length > 0 || isDrawing) && (
            <button
              onClick={polygonHandlers.handlePolygonReset}
              style={buttonStyle}
              title="Reset"
            >
              <i className="fa fa-refresh" style={{ fontSize: '18px' }} />
            </button>
          )}
        </div>
      )}

      <GoogleMap
        options={options}
        mapContainerStyle={containerStyle}
        center={center}
        zoom={17}
        onLoad={handleMapLoad}
        onClick={locationType === 'polygon' && isDrawing ? handleMapClick : setAddressFromMap}
        style={{ cursor: (locationType === 'polygon' && isDrawing) ? 'crosshair' : 'default' }}
      >
        {renderCircle()}
        {renderPolygon()}
      </GoogleMap>
    </div>
  );
};

export default TaskLocationMap;
